import { Dispatch, useState, type FC, useEffect, useCallback, useRef } from "react";
import { useForm, useFormState } from "react-final-form";
import { ClientId, ConfigNamesFields, NamesOfFieldEnum, OrgTypeEnum, ResponseDefaultFields } from "~/typings/types";
import { Modal, FloatingLabelInput, ErrorField } from "~/components";
import { useModal } from "~/hooks";
import { Field } from "react-final-form";
import { clientApi, composeValidators, required, safeLocalStorage, testRegExp } from "~/utils";
import { disabledValidate } from "~/utils/validate";

import styles from "./CustomInnField.module.scss";
import { ModalContent } from "./ModalContent";
import { PERSONAL_DATA_BY_INN } from "~/api/apiUrls";
import { ERROR_FIELD_ } from "~/configs/consts";

const IS_CHECK_INN_SUCCESS = 'is_check_inn_success'

interface Props {
  setIsCorrectFullnameByInn?: Dispatch<boolean>;
  clientId: ClientId;
  innLength: number;
  configNameFields: ConfigNamesFields;
  orgType: OrgTypeEnum;
}

export const CustomInnField: FC<Props> = ({
  clientId,
  setIsCorrectFullnameByInn,
  innLength,
  configNameFields,
  orgType
}) => {
  const ERROR_MSG_INN_LENGTH = `ИНН должен состоять из ${innLength} цифр`;

  const [isErrorField, setIsErrorField] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [personalData, setPersonalData] = useState<ResponseDefaultFields['fields'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prevInnRef = useRef<string>("");
  const isFetchingRef = useRef<boolean>(false);

  const { change } = useForm();
  const { values } = useFormState();
  const removeSpaces = (value: string) => value && value?.trim();

  const {
    isShow: isShowModal,
    open: openModal,
    close: closeModal,
    toggle: toggleModal
  } = useModal();

  const valueInn = values[NamesOfFieldEnum.Inn];
  const valueName = values[NamesOfFieldEnum.Name];

  const fetchPersonalDataByInn = async (inn: string) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      setIsNotFound(false);

      const result = await clientApi.post(PERSONAL_DATA_BY_INN, {
        id: clientId,
        org_type: orgType,
        inn: inn
      });

      if (result?.data?.success) {
        setPersonalData(result?.data?.fields);
        openModal();
      } else {
        setIsNotFound(true);
      }

    } catch (err: any) {
      setIsNotFound(true);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (isInitialLoad) {
      prevInnRef.current = valueInn || "";
      setIsInitialLoad(false);
      return;
    }

    if (prevInnRef.current !== valueInn) {
      if (safeLocalStorage.getItem(IS_CHECK_INN_SUCCESS)) {
        safeLocalStorage.removeItem(IS_CHECK_INN_SUCCESS);
      }

      prevInnRef.current = valueInn || "";
    }
  }, [valueInn, isInitialLoad]);

  const handleFetchAndShowModal = useCallback(async () => {
    if (isFetchingRef.current) return;

    setIsNotFound(false);

    if (!valueInn || valueInn.length !== innLength) {
      setIsErrorField(true);
      return;
    }

    if (safeLocalStorage.getItem(IS_CHECK_INN_SUCCESS)) return;

    try {
      await fetchPersonalDataByInn(valueInn);
    } catch (err) {
      // Ошибка уже обработана в fetchPersonalDataByInn
    }
  }, [valueInn, innLength]);

  useEffect(() => {
    if (error) {
      setIsNotFound(true);
    }
  }, [error]);

  useEffect(() => {
    if (isErrorField && valueInn?.length === innLength) {
      setIsErrorField(false);
    }
  }, [valueInn, isErrorField, innLength]);

  const handlePersonalDataSuccess = useCallback(() => {
    if (personalData) {
      Object.entries(personalData).forEach(([nameField, valueField]) => {
        change(nameField, valueField);
      });

      if (
        (orgType === OrgTypeEnum.Company || orgType === OrgTypeEnum.IndividualEntrepreneur) &&
        personalData.top_manager
      ) {
        const tm = personalData.top_manager;
        if (tm) {
          tm.full_name && change(NamesOfFieldEnum.CrmName, tm.full_name);
          tm.first_name && change(NamesOfFieldEnum.CrmNameFirst, tm.first_name);
          tm.last_name && change(NamesOfFieldEnum.CrmNameLast, tm.last_name);
          tm.patronymic_name && change(NamesOfFieldEnum.CrmNamePatron, tm.patronymic_name);
        }
      }

      safeLocalStorage.setItem(IS_CHECK_INN_SUCCESS, "true");

      if (setIsCorrectFullnameByInn) {
        if (personalData?.top_manager?.full_name === valueName) {
          setIsCorrectFullnameByInn(true);
        } else {
          setIsCorrectFullnameByInn(false);
        }
      }
    }
    closeModal();
  }, [personalData, change, orgType, setIsCorrectFullnameByInn, valueName, closeModal]);

  const handlePersonalDataReject = useCallback(() => {
    closeModal();
  }, [closeModal]);

  return (
    <div className={styles.container} id={isErrorField ? `${ERROR_FIELD_}${NamesOfFieldEnum.Inn}-error` : undefined}>
      <div style={{ width: "100%" }}>
        <Field
          name={NamesOfFieldEnum.Inn}
          component="input"
          type="text"
          validate={disabledValidate(composeValidators(
            required(),
            testRegExp(`^(\\d){${innLength}}$`, ERROR_MSG_INN_LENGTH)
          ))}
          formatOnBlur
          format={removeSpaces}
        >
          {({ input, meta }) => (
            <div>
              <FloatingLabelInput
                {...input}
                meta={meta}
                titleField="Введите ИНН и мы вас найдем"
                required
                maxLength={innLength}
                onBlurCallback={(e: React.FocusEvent<HTMLInputElement>) => {
                  handleFetchAndShowModal();
                }}
              />

              <ErrorField
                name={NamesOfFieldEnum.Inn}
                renderError={
                  isErrorField
                    ? {
                      has: 1,
                      message: ERROR_MSG_INN_LENGTH
                    }
                    : isNotFound
                      ? {
                        has: 1,
                        message: "Мы вас не нашли по ИНН, но вы можете продолжить заполнение анкеты",
                        color: '#8c8e94'
                      }
                      : undefined
                }
              />
            </div>
          )}
        </Field>
      </div>

      <Modal isShow={isShowModal} toggle={toggleModal}>
        <ModalContent
          fields={personalData}
          configNameFields={configNameFields}
          handleClickSuccess={handlePersonalDataSuccess}
          handleClickReject={handlePersonalDataReject}
          orgType={orgType}
        />
      </Modal>
    </div>
  );
};